import { Decimal } from "decimal.js";

Decimal.set({ precision: 48, rounding: Decimal.ROUND_HALF_UP, toExpNeg: -40, toExpPos: 40 });

export const AMORTIZATION_ENGINE_VERSION = "1.1.0";

export type DayCountConvention = "actual_365" | "actual_360" | "30_360" | "actual_actual";
export type PaymentFrequency = "monthly" | "quarterly" | "annual";
export type BusinessDayConvention = "none" | "following" | "modified_following" | "preceding";
export type AccrualFrequency = "daily" | "monthly" | "quarterly" | "annual";
export type CalculationMethod = "simple_periodic" | "daily_simple";
export type CompoundingMethod = "none" | "monthly";
export type PaymentTiming = "advance" | "arrears";

export interface RateSegmentInput {
  annualRate: string;
  effectiveDate: string;
  paymentAmount?: string | null;
}

export interface AdditionalPrincipalInput {
  amount: string;
  date: string;
}

export interface FeeInput {
  amount: string;
  amortizationMethod: "straight_line" | null;
}

export interface AmortizationInput {
  accrualFrequency: AccrualFrequency;
  additionalPrincipal?: AdditionalPrincipalInput[];
  amortizationTermMonths: number;
  balloonAmount?: string;
  balloonMethod?: "fixed" | "remaining_principal";
  businessDayConvention: BusinessDayConvention;
  calculationMethod: CalculationMethod;
  compoundingMethod: CompoundingMethod;
  dayCountConvention: DayCountConvention;
  earlyPayoffDate?: string | null;
  fees?: FeeInput[];
  firstAccrualDate: string;
  firstExpectedBankPaymentDate: string;
  firstPaymentApplicationDate: string;
  interestOnlyEndDate?: string | null;
  maturityDate: string;
  originalPrincipal: string;
  paymentAmount?: string | null;
  paymentFrequency: PaymentFrequency;
  paymentTiming: PaymentTiming;
  rateSegments: RateSegmentInput[];
}

export interface ScheduleValidation {
  code: string;
  message: string;
  periodNumber?: number;
  severity: "error" | "warning";
}

export interface AmortizationPeriod {
  accounting: {
    additionalPrincipal: string;
    balloonAmount: string;
    beginningPrincipal: string;
    cashPayment: string;
    endingPrincipal: string;
    fees: string;
    interestAccrued: string;
    principalApplied: string;
  };
  accrualDate: string;
  additionalPrincipal: string;
  annualRate: string;
  balloonAmount: string;
  beginningPrincipal: string;
  cashPayment: string;
  dayCount: number;
  endingPrincipal: string;
  expectedBankPaymentDate: string;
  fees: string;
  interestAccrued: string;
  interestApplied: string;
  paymentApplicationDate: string;
  periodEnd: string;
  periodNumber: number;
  periodStart: string;
  principalApplied: string;
}

export interface AmortizationResult {
  engineVersion: string;
  periods: AmortizationPeriod[];
  residualAmount: string;
  totalFees: string;
  totalInterest: string;
  totalPrincipal: string;
  validation: ScheduleValidation[];
}

export interface ExpectedPaymentInput {
  amortizationTermMonths: number;
  annualRate: string;
  balloonAmount?: string;
  originalPrincipal: string;
  paymentFrequency: PaymentFrequency;
  paymentTiming: PaymentTiming;
}

export function calculateExpectedPayment(input:ExpectedPaymentInput):string {
  const principal=decimal(input.originalPrincipal);
  const annualRate=decimal(input.annualRate);
  const balloon=decimal(input.balloonAmount??"0");
  if(principal.lessThanOrEqualTo(0)||annualRate.lessThan(0)||balloon.lessThan(0)||balloon.greaterThanOrEqualTo(principal))throw new Error("Expected payment inputs are invalid");
  if(!Number.isInteger(input.amortizationTermMonths)||input.amortizationTermMonths<=0)throw new Error("Amortization term must be a positive whole number of months");
  return money(calculateLevelPayment(principal,annualRate,input.amortizationTermMonths,input.paymentFrequency,balloon,input.paymentTiming));
}

export function generateAmortizationSchedule(input: AmortizationInput): AmortizationResult {
  const validation = validateInput(input);
  if (validation.some((result) => result.severity === "error")) return emptyResult(validation);

  const principal = decimal(input.originalPrincipal);
  const maturityDate = parseDate(input.maturityDate);
  const firstApplicationDate = parseDate(input.firstPaymentApplicationDate);
  const expectedOffsetDays = daysBetween(firstApplicationDate, parseDate(input.firstExpectedBankPaymentDate));
  const firstPeriodEnd=input.paymentTiming==="advance"
    ? addMonths(firstApplicationDate,frequencyMonths(input.paymentFrequency))
    : firstApplicationDate;
  const periodDates = buildPeriodDates(firstPeriodEnd, maturityDate, input.paymentFrequency);
  const expectedPeriodCount=Math.ceil(input.amortizationTermMonths/frequencyMonths(input.paymentFrequency));
  if(periodDates.length!==expectedPeriodCount){
    validation.push({code:"TERM_DATE_MISMATCH",message:`The configured dates produce ${periodDates.length} payments, but the amortization term requires ${expectedPeriodCount}. Align the first payment date, maturity date, and amortization term.`,severity:"error"});
    return emptyResult(validation);
  }
  const straightLineFees = (input.fees ?? [])
    .filter((fee) => fee.amortizationMethod === "straight_line")
    .reduce((total, fee) => total.plus(decimal(fee.amount)), new Decimal(0));
  const feePerPeriod = periodDates.length === 0 ? new Decimal(0) : straightLineFees.div(periodDates.length);
  const balloonTarget = decimal(input.balloonAmount ?? "0");
  const additionalPrincipal = new Map<string, Decimal>();
  for (const item of input.additionalPrincipal ?? []) {
    additionalPrincipal.set(item.date, (additionalPrincipal.get(item.date) ?? new Decimal(0)).plus(decimal(item.amount)));
  }

  const periods: AmortizationPeriod[] = [];
  let balance = principal;
  let periodStart = parseDate(input.firstAccrualDate);
  let totalInterest = new Decimal(0);
  let totalPrincipal = new Decimal(0);
  let totalFees = new Decimal(0);
  let priorAccruedInterest = new Decimal(0);

  for (let index = 0; index < periodDates.length && balance.greaterThan(0); index += 1) {
    const contractualEnd = periodDates[index]!;
    const payoffDate = input.earlyPayoffDate ? parseDate(input.earlyPayoffDate) : null;
    const periodEnd = payoffDate && payoffDate < contractualEnd && payoffDate >= periodStart ? payoffDate : contractualEnd;
    const applicationDate = adjustBusinessDay(input.paymentTiming === "advance" ? periodStart : periodEnd, input.businessDayConvention);
    const expectedBankDate = addDays(applicationDate, expectedOffsetDays);
    const segment = findRateSegment(input.rateSegments, periodStart);
    if (!segment) {
      validation.push({ code: "MISSING_RATE_SEGMENT", message: "No rate applies to this period.", periodNumber: index + 1, severity: "error" });
      break;
    }

    const annualRate = decimal(segment.annualRate);
    const interestOnly = input.interestOnlyEndDate !== null && input.interestOnlyEndDate !== undefined
      && periodEnd <= parseDate(input.interestOnlyEndDate);
    const scheduledPayment = segment.paymentAmount ?? input.paymentAmount;
    const payment = scheduledPayment
      ? decimal(scheduledPayment)
      : decimal(calculateExpectedPayment({amortizationTermMonths:input.amortizationTermMonths,annualRate:raw(annualRate),balloonAmount:raw(balloonTarget),originalPrincipal:raw(principal),paymentFrequency:input.paymentFrequency,paymentTiming:input.paymentTiming}));
    const isFinal = periodEnd >= maturityDate || (payoffDate !== null && periodEnd >= payoffDate);
    const targetBalance = isFinal
      ? input.balloonMethod === "remaining_principal" ? balance : Decimal.min(balloonTarget, balance)
      : balloonTarget;
    const interestApplied=input.paymentTiming==="advance"?priorAccruedInterest:calculatePeriodInterest(balance,annualRate,periodStart,periodEnd,input);
    let scheduledPrincipal = interestOnly ? new Decimal(0) : Decimal.max(payment.minus(interestApplied), 0);
    scheduledPrincipal = Decimal.min(scheduledPrincipal, Decimal.max(balance.minus(targetBalance), 0));
    let extraPrincipal = additionalPrincipal.get(toIsoDate(applicationDate)) ?? new Decimal(0);
    extraPrincipal = Decimal.min(extraPrincipal, balance.minus(scheduledPrincipal));
    let balloon = new Decimal(0);

    if (isFinal) {
      if (payoffDate !== null && periodEnd >= payoffDate) {
        scheduledPrincipal = balance.minus(extraPrincipal);
      } else if (input.balloonMethod === "remaining_principal") {
        balloon = balance.minus(scheduledPrincipal).minus(extraPrincipal);
      } else {
        scheduledPrincipal = Decimal.max(balance.minus(extraPrincipal).minus(balloonTarget), 0);
        balloon = Decimal.min(balloonTarget, balance.minus(scheduledPrincipal).minus(extraPrincipal));
      }
    }

    const endingBalance = Decimal.max(balance.minus(scheduledPrincipal).minus(extraPrincipal).minus(balloon), 0);
    const interest = input.paymentTiming==="advance"
      ? calculatePeriodInterest(endingBalance,annualRate,periodStart,periodEnd,input)
      : interestApplied;
    const fee = index === periodDates.length - 1 ? straightLineFees.minus(totalFees) : feePerPeriod;
    const cashPayment = interestApplied.plus(scheduledPrincipal).plus(extraPrincipal).plus(balloon).plus(fee);
    const period = createPeriod({
      additionalPrincipal: extraPrincipal,
      annualRate,
      balloon,
      beginningPrincipal: balance,
      cashPayment,
      dayCount: dayCount(periodStart, periodEnd, input.dayCountConvention),
      endingPrincipal: endingBalance,
      expectedBankDate,
      fee,
      interest,
      interestApplied,
      paymentApplicationDate: applicationDate,
      periodEnd,
      periodNumber: index + 1,
      periodStart,
      principalApplied: scheduledPrincipal,
    });
    periods.push(period);
    totalInterest = totalInterest.plus(interest);
    totalPrincipal = totalPrincipal.plus(scheduledPrincipal).plus(extraPrincipal).plus(balloon);
    totalFees = totalFees.plus(fee);
    priorAccruedInterest=input.paymentTiming==="advance"?interest:new Decimal(0);
    balance = endingBalance;
    periodStart = periodEnd;

    if (!interestOnly && !isFinal && payment.lessThanOrEqualTo(interestApplied)) {
      validation.push({ code: "PAYMENT_DOES_NOT_AMORTIZE", message: "The payment does not reduce principal.", periodNumber: index + 1, severity: "error" });
    }
    if (payoffDate !== null && periodEnd >= payoffDate) break;
  }

  if (balance.greaterThan("0.005")) {
    validation.push({ code: "PRINCIPAL_REMAINS", message: "Principal remains after the final schedule period.", severity: "error" });
  }
  const principalDifference = principal.minus(totalPrincipal);
  if (principalDifference.abs().greaterThan("0.005")) {
    validation.push({ code: "ROUNDING_RESIDUAL", message: "The schedule has a principal residual greater than half a cent.", severity: "error" });
  }

  return {
    engineVersion: AMORTIZATION_ENGINE_VERSION,
    periods,
    residualAmount: raw(principalDifference),
    totalFees: raw(totalFees),
    totalInterest: raw(totalInterest),
    totalPrincipal: raw(totalPrincipal),
    validation,
  };
}

export function calculateYearFraction(start: Date, end: Date, convention: DayCountConvention): Decimal {
  if (convention === "30_360") return new Decimal(dayCount(start, end, convention)).div(360);
  if (convention === "actual_360") return new Decimal(daysBetween(start, end)).div(360);
  if (convention === "actual_365") return new Decimal(daysBetween(start, end)).div(365);

  let cursor = start;
  let fraction = new Decimal(0);
  while (cursor < end) {
    const nextYear = utcDate(cursor.getUTCFullYear() + 1, 0, 1);
    const segmentEnd = nextYear < end ? nextYear : end;
    fraction = fraction.plus(new Decimal(daysBetween(cursor, segmentEnd)).div(isLeapYear(cursor.getUTCFullYear()) ? 366 : 365));
    cursor = segmentEnd;
  }
  return fraction;
}

function validateInput(input: AmortizationInput): ScheduleValidation[] {
  const validation: ScheduleValidation[] = [];
  let principal: Decimal;
  try { principal = decimal(input.originalPrincipal); } catch { return [{ code: "PRINCIPAL_INVALID", message: "Principal must be a decimal value.", severity: "error" }]; }
  if (principal.lessThanOrEqualTo(0)) validation.push({ code: "PRINCIPAL_INVALID", message: "Principal must be greater than zero.", severity: "error" });
  if (input.rateSegments.length === 0) validation.push({ code: "MISSING_RATE", message: "At least one rate segment is required.", severity: "error" });
  const dates = [input.firstAccrualDate, input.firstPaymentApplicationDate, input.firstExpectedBankPaymentDate, input.maturityDate];
  if (dates.some((value) => !isIsoDate(value))) validation.push({ code: "DATE_INVALID", message: "Schedule dates must use YYYY-MM-DD.", severity: "error" });
  if (validation.length === 0 && (parseDate(input.firstAccrualDate) > parseDate(input.firstPaymentApplicationDate)
    || parseDate(input.firstPaymentApplicationDate) > parseDate(input.maturityDate))) {
    validation.push({ code: "DATE_ORDER_INVALID", message: "Accrual, payment, and maturity dates are out of order.", severity: "error" });
  }
  if(validation.length===0&&input.paymentTiming==="advance"&&input.firstAccrualDate!==input.firstPaymentApplicationDate){
    validation.push({code:"ADVANCE_PAYMENT_DATE_INVALID",message:"For payments in advance, the first payment application date must equal the first accrual date.",severity:"error"});
  }
  if (!Number.isInteger(input.amortizationTermMonths) || input.amortizationTermMonths <= 0) {
    validation.push({ code: "TERM_INVALID", message: "Amortization term must be a positive whole number of months.", severity: "error" });
  }
  return validation;
}

function buildPeriodDates(firstDate: Date, maturityDate: Date, frequency: PaymentFrequency): Date[] {
  const dates: Date[] = [];
  const months = frequencyMonths(frequency);
  let date = firstDate;
  while (date < maturityDate) {
    dates.push(date);
    const next = addMonths(firstDate, months * dates.length);
    date = next > date ? next : addDays(date, 1);
  }
  if (dates.length === 0 || dates[dates.length - 1]!.getTime() !== maturityDate.getTime()) dates.push(maturityDate);
  return dates;
}

function findRateSegment(segments: RateSegmentInput[], date: Date): RateSegmentInput | undefined {
  return [...segments]
    .filter((segment) => parseDate(segment.effectiveDate) <= date)
    .sort((left, right) => right.effectiveDate.localeCompare(left.effectiveDate))[0];
}

function calculateLevelPayment(
  principal: Decimal,
  annualRate: Decimal,
  amortizationMonths: number,
  frequency: PaymentFrequency,
  balloon: Decimal,
  paymentTiming: PaymentTiming,
): Decimal {
  const periodsPerYear = new Decimal(12).div(frequencyMonths(frequency));
  const periodRate = annualRate.div(periodsPerYear);
  const periodCount = new Decimal(amortizationMonths).div(frequencyMonths(frequency)).ceil();
  if (periodRate.isZero()) return principal.minus(balloon).div(periodCount);
  const discount = new Decimal(1).plus(periodRate).pow(periodCount.negated());
  const arrearsPayment=principal.minus(balloon.mul(discount)).mul(periodRate).div(new Decimal(1).minus(discount));
  return paymentTiming==="advance"?arrearsPayment.div(new Decimal(1).plus(periodRate)):arrearsPayment;
}

function calculatePeriodInterest(balance:Decimal,annualRate:Decimal,start:Date,end:Date,input:AmortizationInput):Decimal {
  if(input.compoundingMethod==="monthly"){
    const monthlyRate=annualRate.div(12);
    return balance.mul(new Decimal(1).plus(monthlyRate).pow(calendarMonthsBetween(start,end)).minus(1));
  }
  if(input.calculationMethod==="daily_simple"||input.accrualFrequency==="daily"){
    return balance.mul(annualRate).mul(calculateYearFraction(start,end,input.dayCountConvention));
  }
  const monthsPerAccrual=frequencyMonths(input.accrualFrequency);
  const accrualPeriods=calendarMonthsBetween(start,end).div(monthsPerAccrual);
  return balance.mul(annualRate.div(new Decimal(12).div(monthsPerAccrual))).mul(accrualPeriods);
}

function calendarMonthsBetween(start:Date,end:Date):Decimal {
  let wholeMonths=(end.getUTCFullYear()-start.getUTCFullYear())*12+end.getUTCMonth()-start.getUTCMonth();
  while(wholeMonths>0&&addMonths(start,wholeMonths)>end)wholeMonths-=1;
  const anchor=addMonths(start,wholeMonths);
  if(anchor.getTime()===end.getTime())return new Decimal(wholeMonths);
  const next=addMonths(anchor,1);
  return new Decimal(wholeMonths).plus(new Decimal(daysBetween(anchor,end)).div(daysBetween(anchor,next)));
}

function createPeriod(values: {
  additionalPrincipal: Decimal; annualRate: Decimal; balloon: Decimal; beginningPrincipal: Decimal; cashPayment: Decimal;
  dayCount: number; endingPrincipal: Decimal; expectedBankDate: Date; fee: Decimal; interest: Decimal; interestApplied:Decimal;
  paymentApplicationDate: Date; periodEnd: Date; periodNumber: number; periodStart: Date; principalApplied: Decimal;
}): AmortizationPeriod {
  return {
    accounting: {
      additionalPrincipal: money(values.additionalPrincipal), balloonAmount: money(values.balloon),
      beginningPrincipal: money(values.beginningPrincipal), cashPayment: money(values.cashPayment),
      endingPrincipal: money(values.endingPrincipal), fees: money(values.fee), interestAccrued: money(values.interest),
      principalApplied: money(values.principalApplied),
    },
    accrualDate: toIsoDate(values.periodEnd), additionalPrincipal: raw(values.additionalPrincipal), annualRate: rate(values.annualRate),
    balloonAmount: raw(values.balloon), beginningPrincipal: raw(values.beginningPrincipal), cashPayment: raw(values.cashPayment),
    dayCount: values.dayCount, endingPrincipal: raw(values.endingPrincipal), expectedBankPaymentDate: toIsoDate(values.expectedBankDate),
    fees: raw(values.fee), interestAccrued: raw(values.interest), interestApplied: raw(values.interestApplied),
    paymentApplicationDate: toIsoDate(values.paymentApplicationDate), periodEnd: toIsoDate(values.periodEnd),
    periodNumber: values.periodNumber, periodStart: toIsoDate(values.periodStart), principalApplied: raw(values.principalApplied),
  };
}

function dayCount(start: Date, end: Date, convention: DayCountConvention): number {
  if (convention !== "30_360") return daysBetween(start, end);
  const startDay = Math.min(start.getUTCDate(), 30);
  const endDay = startDay === 30 ? Math.min(end.getUTCDate(), 30) : end.getUTCDate();
  return (end.getUTCFullYear() - start.getUTCFullYear()) * 360
    + (end.getUTCMonth() - start.getUTCMonth()) * 30 + endDay - startDay;
}

function adjustBusinessDay(date: Date, convention: BusinessDayConvention): Date {
  if (convention === "none" || !isWeekend(date)) return date;
  const direction = convention === "preceding" ? -1 : 1;
  let adjusted = addDays(date, direction);
  while (isWeekend(adjusted)) adjusted = addDays(adjusted, direction);
  if (convention === "modified_following" && adjusted.getUTCMonth() !== date.getUTCMonth()) {
    adjusted = addDays(date, -1);
    while (isWeekend(adjusted)) adjusted = addDays(adjusted, -1);
  }
  return adjusted;
}

function addMonths(date: Date, months: number): Date {
  const targetMonth = date.getUTCMonth() + months;
  const targetYear = date.getUTCFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, normalizedMonth + 1, 0)).getUTCDate();
  return utcDate(targetYear, normalizedMonth, Math.min(date.getUTCDate(), lastDay));
}

function addDays(date: Date, days: number): Date { return new Date(date.getTime() + days * 86_400_000); }
function daysBetween(start: Date, end: Date): number { return Math.round((end.getTime() - start.getTime()) / 86_400_000); }
function decimal(value: string): Decimal { return new Decimal(value); }
function frequencyMonths(frequency: PaymentFrequency | Exclude<AccrualFrequency,"daily">): number { return frequency === "monthly" ? 1 : frequency === "quarterly" ? 3 : 12; }
function isIsoDate(value: string): boolean { return /^\d{4}-\d{2}-\d{2}$/u.test(value) && toIsoDate(parseDate(value)) === value; }
function isLeapYear(year: number): boolean { return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0); }
function isWeekend(date: Date): boolean { return date.getUTCDay() === 0 || date.getUTCDay() === 6; }
function money(value: Decimal): string { return value.toDecimalPlaces(2).toFixed(2); }
function parseDate(value: string): Date { return new Date(`${value}T00:00:00.000Z`); }
function rate(value: Decimal): string { return value.toDecimalPlaces(16).toFixed(16); }
function raw(value: Decimal): string { return value.toDecimalPlaces(12).toFixed(12); }
function toIsoDate(value: Date): string { return value.toISOString().slice(0, 10); }
function utcDate(year: number, month: number, day: number): Date { return new Date(Date.UTC(year, month, day)); }

function emptyResult(validation: ScheduleValidation[]): AmortizationResult {
  return { engineVersion: AMORTIZATION_ENGINE_VERSION, periods: [], residualAmount: "0.000000000000", totalFees: "0.000000000000", totalInterest: "0.000000000000", totalPrincipal: "0.000000000000", validation };
}